import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Dialog from "@/v2-ui/dialog"
import { errorToast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import TooltipLink from "@/v2-backoffice-client/components/link/TooltipLink"
import { getTranslationViewHref } from "@/v2-backoffice-client/translation/translation.utils"
import { useTranslationPublishMutation } from "@/v2-backoffice-client/translation/publish/translation.publish.mutation"
import { useTranslationPublishRunsQuery } from "@/v2-backoffice-client/translation/publish/translation.publishRuns.query"
import { useCatalogChangesQuery } from "@/v2-backoffice-client/translation/publish/translation.changes.query"
import {
  CURRENT_PUBLISH_ENVIRONMENT,
  findPublishBaseline,
  PUBLISH_ENVIRONMENTS,
  summarisePendingChanges,
  targetIncludesCurrentEnvironment,
  type PublishEnvironment,
  type PublishRun,
} from "@/v2-backoffice-client/translation/publish/translation.publish.utils"

// A run is live while GitHub still reports it queued / in_progress.
function isRunning(run: PublishRun) {
  return run.Status !== "completed"
}

function runVariant(run: PublishRun) {
  // No "warning" token exists in the shared palette; running states use the
  // muted tone and the label text ("queued" / "in progress") carries meaning.
  if (isRunning(run)) return "text-muted-foreground"
  if (run.Conclusion === "success") return "text-primary"
  return "text-destructive"
}

function runLabel(run: PublishRun) {
  return isRunning(run) ? run.Status.replace("_", " ") : (run.Conclusion ?? "?")
}

function formatRunTime(createdAt: string) {
  return createdAt.replace("T", " ").replace("Z", "")
}

type PendingLineProps = {
  label: string
  labelTitle: string
  count: number
  note: string
}

// One facet group of the publish preview: what changed, how much, and whether
// it is actually waiting on this publish.
function PendingLine(props: PendingLineProps) {
  const { label, labelTitle, count, note } = props
  return (
    <div className="flex items-baseline gap-2">
      <span
        className="w-32 shrink-0 font-medium"
        title={labelTitle}
        role="term"
      >
        {label}
      </span>
      <span
        className={cn("tabular-nums", count === 0 && "text-muted-foreground")}
      >
        {count === 0 ? "no changes" : `${count} change(s)`}
      </span>
      <span className="text-muted-foreground">— {note}</span>
    </div>
  )
}

// "Publish…" opens the publish dialog: pick the target environment, fire the
// Translation Publish GitHub workflow (workflow_dispatch), and watch its
// recent runs live. The pipeline (export -> validate -> blob -> reseed) runs
// entirely in CI; this button only triggers and observes it.
function TranslationPublishButton() {
  const [open, setOpen] = useState(false)
  const [environment, setEnvironment] = useState<PublishEnvironment>(
    CURRENT_PUBLISH_ENVIRONMENT,
  )
  const [dispatched, setDispatched] = useState(false)

  const [publish, publishState] = useTranslationPublishMutation()
  errorToast(publishState.error, { title: "Publish trigger failed" })

  const runsQuery = useTranslationPublishRunsQuery({
    skip: !open,
    // Live status while the dialog is open; runs finish in minutes.
    pollInterval: 5000,
    fetchPolicy: "cache-and-network",
  })
  const runList = runsQuery.data?.getTranslationPublishRuns
  const runs = runList?.Runs ?? []
  const isConfigured = runList?.Configured ?? true

  // Baseline for the pending changelog, per target environment: publishing to
  // dev must not silence what is still pending for prod. Dispatching a new
  // publish moves the baseline only once that run succeeds, which is exactly
  // right.
  const baseline = findPublishBaseline(runs, environment)
  const changesQuery = useCatalogChangesQuery({
    variables: { since: baseline?.CreatedAt ?? "" },
    skip: !open || !baseline,
  })
  const changes = changesQuery.data?.getCatalogChangesSince
  const summary = changes ? summarisePendingChanges(changes) : undefined
  const shipsHere = targetIncludesCurrentEnvironment(environment)

  async function onPublish() {
    const res = await publish({ variables: { environment } })
    if (res.data?.publishTranslations) {
      setDispatched(true)
      void runsQuery.refetch()
    }
  }

  return (
    <>
      <Button
        size="sm"
        iconSrc="rocket"
        data-feature-code="UI_BackofficeTranslation_Publish"
        onClick={() => setOpen(true)}
        title="Ship the current v2-features-db state to the runtime: the publish workflow exports the catalog, validates, uploads the settings blobs, and reseeds the runtime databases. Every column ships, not only text."
      >
        Publish catalog…
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setDispatched(false)
        }}
      >
        <Dialog.Content className="w-[680px] max-w-[92vw]">
          <Dialog.Header>
            <Dialog.Title>Publish the catalog</Dialog.Title>
            <Dialog.Description>
              Runs the Translation Publish GitHub workflow: export from
              v2-features-db → validate (fail-closed) → upload the settings
              blobs → reseed the runtime databases of the chosen environment.
              The reseed replaces every published column — text, configuration,
              availability, personal data and countries — from the catalog. It
              never changes what customers see: a layer new to an environment
              lands hidden in that environment&apos;s review queue, and &quot;Go
              live&quot; there is what shows it.
            </Dialog.Description>
          </Dialog.Header>

          <div className="flex flex-col gap-4 py-2">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">Reseed environment</span>
              {PUBLISH_ENVIRONMENTS.map((env) => (
                <label key={env} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="publish-environment"
                    checked={environment === env}
                    onChange={() => setEnvironment(env)}
                    data-feature-code="UI_BackofficeTranslation_Publish_Environment"
                  />
                  {env}
                </label>
              ))}
              <div className="flex-1" />
              <Button
                size="sm"
                data-feature-code="UI_BackofficeTranslation_Publish_Start"
                onClick={onPublish}
                disabled={publishState.loading || !isConfigured}
                isLoading={publishState.loading}
                title="Fire a workflow_dispatch of the Translation Publish workflow with the chosen environment. The blob upload is always global; the environment selects which runtime databases are reseeded."
              >
                {publishState.loading
                  ? "Starting…"
                  : `Publish to ${environment}`}
              </Button>
            </div>

            {/* What this publish ships, split by whether it is actually
                waiting on the publish. Text is authored here and nowhere
                else; the other facets are saved straight to the runtime and
                only travel to OTHER environments with a reseed — an operator
                who edits config in dev and reads a single "12 pending" would
                otherwise think none of it had landed. */}
            <div className="flex flex-col gap-1.5 rounded-md border border-border/60 px-3 py-2 text-xs">
              {!baseline ? (
                <span className="text-muted-foreground">
                  No successful publish to {environment} yet — this run ships
                  the full current state of the catalog.
                </span>
              ) : summary === undefined ? (
                <span className="text-muted-foreground">
                  Loading what this publish will ship…
                </span>
              ) : (
                <>
                  <PendingLine
                    label="Text"
                    labelTitle="Names and descriptions in every language (common.Translations)."
                    count={summary.textCount}
                    note="edited here only, so it reaches the apps when this publish ships it"
                  />
                  <PendingLine
                    label="Feature settings"
                    labelTitle="Configuration, availability, personal data and countries (common.Features, common.FeaturesCountries)."
                    count={summary.runtimeCount}
                    note={
                      shipsHere
                        ? `saved straight to ${CURRENT_PUBLISH_ENVIRONMENT}, so already live here — re-shipped unchanged`
                        : `live in ${CURRENT_PUBLISH_ENVIRONMENT}, reaching ${environment} with this run`
                    }
                  />
                </>
              )}

              <div className="mt-1 flex items-center gap-2 border-t border-border/60 pt-1.5">
                <span className="text-muted-foreground">
                  {baseline
                    ? `Measured against the last publish to ${environment} (${formatRunTime(baseline.CreatedAt)} UTC).`
                    : "Nothing to measure against yet."}
                </span>
                <div className="flex-1" />
                <TooltipLink
                  to={getTranslationViewHref("pending")}
                  size="tiny"
                  variant="secondary"
                  data-feature-code="UI_BackofficeTranslation_Publish_ReviewPending"
                  onClick={() => {
                    setOpen(false)
                  }}
                  title="Open the Review pending view: every change with a diff against the published state, text adjustable in the inline editor and revertible per line."
                >
                  Review changes
                </TooltipLink>
              </div>
            </div>

            {!isConfigured ? (
              <p className="text-xs text-destructive">
                Publishing is not configured on this API:
                TRANSLATION_GITHUB_SYNC_TOKEN is missing, so the workflow can
                neither be triggered nor observed from here.
              </p>
            ) : null}

            {dispatched ? (
              <p className="text-xs text-muted-foreground">
                Workflow triggered — the run appears below within a few seconds
                and typically finishes in a few minutes.
              </p>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Recent runs</span>
              {/* Inline (not a toast): the query polls, and a persistent
                  backend problem would re-toast every 5 seconds. */}
              {runsQuery.error && runs.length === 0 ? (
                <p className="text-xs text-destructive">
                  Could not load runs: {runsQuery.error.message}
                </p>
              ) : runs.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {runsQuery.loading ? "Loading…" : "No runs yet."}
                </p>
              ) : (
                runs.map((run) => (
                  <div
                    key={run.Id}
                    className="flex items-center justify-between gap-3 text-xs"
                  >
                    <span className="text-muted-foreground tabular-nums">
                      {formatRunTime(run.CreatedAt)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {run.DisplayName}
                    </span>
                    <span className={cn("font-medium", runVariant(run))}>
                      {runLabel(run)}
                    </span>
                    <a
                      href={run.HtmlUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-primary hover:underline"
                    >
                      Open in GitHub ↗
                    </a>
                  </div>
                ))
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export default TranslationPublishButton
